import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Cart, Dash, Plus, Trash } from 'react-bootstrap-icons';
import useCartContant from './useCartContant';
import { apiUrl, mediaUrl } from '../../config/api';
import { readAuth } from '../../hooks/useAuthStorage';
import { useShopUI } from '../../context/ShopUIContext';
import './Cart.scss';

const money = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
};

// price_voucher === 1 means a flat amount off; otherwise discount_percent is a percentage.
const couponLabel = (coupon) =>
  Number(coupon?.price_voucher) === 1
    ? `$${money(coupon.discount_percent)} off`
    : `${coupon?.discount_percent}% off`;

const CartModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const { openLogin } = useShopUI();
  const { cartproducts, loading, fetchCartProducts } = useCartContant();

  // Tracks the line item currently being mutated so its row can show a
  // pending state and reject repeat clicks.
  const [pendingKey, setPendingKey] = useState(null);
  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    if (show) fetchCartProducts();
  }, [show, fetchCartProducts]);

  const mutateCart = useCallback(
    async (key, path, method, body) => {
      if (pendingKey) return;
      setPendingKey(key);

      try {
        const response = await fetch(apiUrl(path), {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error('Network response was not ok');
        await fetchCartProducts();
      } catch (error) {
        // The cart is re-fetched either way so the UI never drifts
        // from the server's view of the basket.
        await fetchCartProducts();
      } finally {
        setPendingKey(null);
      }
    },
    [pendingKey, fetchCartProducts]
  );

  const updateQuantity = (variantId, productCode, action) => {
    const { user_id, common_id } = readAuth();
    return mutateCart(`${variantId}-${action}`, '/api/cart/quantity', 'POST', {
      common_id,
      user_id: parseInt(user_id, 10),
      variant_id: variantId,
      product_code: productCode,
      action,
    });
  };

  const removeProduct = (variantId, productCode) => {
    const { user_id, common_id } = readAuth();
    return mutateCart(`${variantId}-remove`, '/api/cart/delete', 'DELETE', {
      common_id,
      user_id: parseInt(user_id, 10),
      product_code: productCode,
      variant_id: variantId,
    });
  };

  const clearCart = async () => {
    if (isClearing) return;
    setIsClearing(true);
    const { user_id, common_id } = readAuth();

    try {
      const response = await fetch(apiUrl('/api/cart/delete-all'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ common_id, user_id: parseInt(user_id, 10) }),
      });
      if (!response.ok) throw new Error('Network response was not ok');
    } finally {
      await fetchCartProducts();
      setIsClearing(false);
    }
  };

  const handleCheckout = () => {
    onClose();
    const { token, user_id } = readAuth();
    if (!token && !user_id) {
      openLogin({ checkout: true });
    } else {
      navigate('/check/checkoutpage');
    }
  };

  const items = cartproducts?.cart_items || [];
  const itemCount = items.length;
  const couponApplied =
    cartproducts?.coupon_applied !== 'N/A' ? cartproducts?.coupon_applied : null;

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable centered className="cart-modal">
      <Modal.Header closeButton>
        <div className="cart-modal__title">
          <h2>Your cart</h2>
          <span className="badge badge--neutral">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading && itemCount === 0 ? (
          <div className="cart-modal__skeletons" aria-busy="true">
            {Array.from({ length: 2 }).map((_, index) => (
              <div className="cart-line" key={index}>
                <span className="skeleton cart-line__media" />
                <div style={{ flex: 1 }}>
                  <div className="skeleton skeleton--title" />
                  <div className="skeleton skeleton--text" style={{ width: '45%' }} />
                  <div className="skeleton skeleton--text" style={{ width: '30%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : itemCount === 0 ? (
          <div className="state-block">
            <span className="state-block__icon">
              <Cart size={28} aria-hidden="true" />
            </span>
            <h3 className="state-block__title">Your cart is empty</h3>
            <p className="state-block__message">
              Browse the collection and add something you love.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                onClose();
                navigate('/allproducts');
              }}
            >
              Start shopping
            </button>
          </div>
        ) : (
          <div className="cart-modal__layout">
            {/* ---- Line items ---- */}
            <ul className="cart-modal__items">
              {items.map((item) => {
                const variantId = item?.variant?.id;
                const image = mediaUrl(item?.product?.product_image_1);
                const variantLabel = [item?.variant?.size, item?.variant?.pieces && `${item.variant.pieces} pieces`]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <li className="cart-line" key={`${item?.product?.product_code}-${variantId}`}>
                    {image ? (
                      <img className="cart-line__media" src={image} alt={item?.product?.product_name} loading="lazy" />
                    ) : (
                      <span className="cart-line__media" aria-hidden="true" />
                    )}

                    <div className="cart-line__body">
                      <h3 className="cart-line__title">{item?.product?.product_name}</h3>
                      {variantLabel && <p className="cart-line__variant">{variantLabel}</p>}
                      <p className="cart-line__price">${money(item?.product_total_price)}</p>

                      <div className="quantity-stepper">
                        <button
                          type="button"
                          onClick={() => updateQuantity(variantId, item.product.product_code, 'decrement')}
                          disabled={Boolean(pendingKey) || item.product_cart_quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          <Dash size={16} aria-hidden="true" />
                        </button>
                        <span aria-live="polite">{item.product_cart_quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(variantId, item.product.product_code, 'increment')}
                          disabled={Boolean(pendingKey)}
                          aria-label="Increase quantity"
                        >
                          <Plus size={16} aria-hidden="true" />
                        </button>
                      </div>

                      {item.adjustment_message && (
                        <p className="cart-line__note">{item.adjustment_message}</p>
                      )}
                    </div>

                    <button
                      type="button"
                      className="cart-line__remove"
                      onClick={() => removeProduct(variantId, item.product.product_code)}
                      disabled={Boolean(pendingKey)}
                      aria-label={`Remove ${item?.product?.product_name} from cart`}
                    >
                      <Trash size={16} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* ---- Summary ---- */}
            <aside className="cart-summary" aria-label="Order summary">
              <h3 className="cart-summary__heading">Summary</h3>

              <dl className="cart-summary__rows">
                <div>
                  <dt>Subtotal</dt>
                  <dd>${money(cartproducts?.cart_sum)}</dd>
                </div>

                {couponApplied?.discount_coupon && (
                  <>
                    <div className="cart-summary__row--positive">
                      <dt>
                        Coupon {couponApplied.discount_coupon} ({couponLabel(couponApplied)})
                      </dt>
                      <dd>
                        −$
                        {money(
                          Math.max(0, Number(cartproducts?.cart_sum) - Number(cartproducts?.discounted_price))
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>After discount</dt>
                      <dd>${money(cartproducts?.discounted_price)}</dd>
                    </div>
                  </>
                )}

                {cartproducts?.tax_percent !== undefined && (
                  <div>
                    <dt>Tax</dt>
                    <dd>{cartproducts.tax_percent}%</dd>
                  </div>
                )}

                <div className={cartproducts?.free_shipping ? 'cart-summary__row--positive' : undefined}>
                  <dt>Shipping</dt>
                  <dd>
                    {cartproducts?.free_shipping
                      ? 'Free'
                      : `$${money(cartproducts?.standard_shipping_price)}`}
                  </dd>
                </div>
              </dl>

              <div className="cart-summary__total">
                <span>Total</span>
                <strong>${money(cartproducts?.subtotal_after_tax)}</strong>
              </div>

              <button type="button" className="btn btn--primary btn--block btn--lg" onClick={handleCheckout}>
                Checkout
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--block btn--sm"
                onClick={clearCart}
                disabled={isClearing}
                aria-busy={isClearing}
              >
                {isClearing ? 'Clearing…' : 'Clear cart'}
              </button>
            </aside>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CartModal;
